import type { RecipeShelfLife, ShelfLifeUnit } from "@/types/recipe";

/**
 * When a stage's shelf life runs out.
 *
 * Days move the calendar date rather than adding 24-hour blocks, so an item
 * prepped the day the clocks change still expires at the same time of day.
 * Hours are added as elapsed time, because a two-hour hot hold is two hours of
 * food sitting in a well regardless of what the calendar does (T-1004).
 */
export function shelfLifeEnds(prep: Date, value: number, unit: ShelfLifeUnit): Date {
  const out = new Date(prep.getTime());
  if (unit === "DAYS") out.setDate(out.getDate() + value);
  else out.setTime(out.getTime() + value * 60 * 60 * 1000);
  return out;
}

/**
 * The use-by as it reads on a sticker.
 *
 * An hours-based stage prints a clock time — a hot hold that says only "today"
 * tells the line cook nothing, since the whole point is that it expires during
 * the shift. A days-based stage prints a date alone.
 */
export function formatUseBy(date: Date, unit: ShelfLifeUnit): string {
  const day = date.toLocaleDateString();
  if (unit === "DAYS") return day;
  return `${day} ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

type ShelfLifeFacts = Pick<
  RecipeShelfLife,
  "shelfLifeValue" | "shelfLifeUnit" | "usedByDate"
>;

/**
 * What the label's Use by line says.
 *
 * A stage carrying a number and a unit gets a computed date; that is the point
 * of T-1004. A stage that predates it has only the typed rule ("End of
 * service"), and printing that verbatim beats printing nothing.
 */
export function labelUseBy(
  stage: ShelfLifeFacts | undefined,
  prep: Date,
): string | undefined {
  const value = stage?.shelfLifeValue;
  const unit = stage?.shelfLifeUnit;
  if (value != null && value > 0 && unit) {
    return formatUseBy(shelfLifeEnds(prep, value, unit), unit);
  }
  return stage?.usedByDate;
}
